import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth';
import {
  getClaimOffer,
  getMyWaitlistEntries,
  getWaitlistEligibility,
  getWaitlistEntry,
  type WaitlistEntry,
} from '@/services/waitlist';
import { canReadWaitlistEntries } from '@/helpers/waitlist';
import { isLoginRequired } from '@/lib/http-adapter';
import useTenant from '@/state/tenant';
import { USER_QUERY_KEY } from '@/state/user';

// `fresh` skips the reuse window for a reader that knows the cached list
// predates what it is looking for.
export default function useMyWaitlistEntries({
  enabled = true,
  fresh = false,
} = {}) {
  const { isAuthenticated, user } = useAuth();
  const { data: tenant } = useTenant();
  return useQuery({
    queryKey: ['waitlist', 'mine', user?.user_id ?? null],
    queryFn: () => getMyWaitlistEntries(),
    enabled: enabled && isAuthenticated && canReadWaitlistEntries(tenant),
    refetchOnMount: true,
    // Her own actions invalidate the list, so the only change it can miss is
    // a server promotion, which email and SMS announce anyway. Reuse keeps the
    // many mounts across list, calendar and dashboard off the read limiter.
    staleTime: fresh ? 0 : 30_000,
  });
}

export const waitlistEntryQueryKey = (entryId: string) => [
  'waitlist',
  'entry',
  entryId,
];

// Only a live offer moves on its own. In CONFIRM mode nobody else can take it,
// so one read just after the deadline is enough; in CLAIM mode a rival can, so
// the page keeps checking. Past the deadline only the once-a-minute expiry
// sweep moves the row, so the page checks often enough to show the closed
// place soon after. A dead session stops the poll: every read would 401.
const offerPollInterval = ({
  data: entry,
  error,
}: {
  data: WaitlistEntry | null | undefined;
  error: unknown;
}): number | false => {
  if (isLoginRequired(error)) return false;
  if (!entry || entry.status !== 'OFFERED' || !entry.offer_expires_at) {
    return false;
  }
  const expiresIn = new Date(entry.offer_expires_at).getTime() - Date.now();
  if (expiresIn <= 0) return 15_000;
  if (entry.mode === 'CLAIM') {
    return Math.min(15_000, Math.max(5_000, expiresIn + 1_000));
  }
  return expiresIn + 1_000;
};

export function useWaitlistEntry(entryId: string) {
  return useQuery({
    queryKey: waitlistEntryQueryKey(entryId),
    // React Query rejects undefined data; null encodes "not found".
    queryFn: async () => (await getWaitlistEntry(entryId)) ?? null,
    refetchOnMount: true,
    refetchInterval: (query) => offerPollInterval(query.state),
  });
}

type EligibilityParams = Parameters<typeof getWaitlistEligibility>[0];

// The probe only runs signed in, so LOGIN_REQUIRED means the cached user
// outlived the access cookie. Re-reading /me lets its 401 run the adapter's
// refresh when one is still possible, and the probe's second answer stands
// either way: a cached user is no proof the session came back, since a /me
// 401 under the refresh latch clears nothing.
const getEligibilityRecheckingSession = async (
  queryClient: QueryClient,
  params: EligibilityParams
) => {
  const eligibility = await getWaitlistEligibility(params);
  if (eligibility?.reason !== 'LOGIN_REQUIRED') return eligibility;
  await queryClient.refetchQueries({ queryKey: USER_QUERY_KEY });
  return getWaitlistEligibility(params);
};

export function useWaitlistEligibility(params: EligibilityParams) {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const occurrenceId = params.slot_id ?? params.override_slot_id ?? null;
  return useQuery({
    queryKey: [
      'waitlist',
      'eligibility',
      params.class_id,
      occurrenceId,
      params.start_time,
      user?.user_id ?? null,
    ],
    queryFn: () => getEligibilityRecheckingSession(queryClient, params),
    enabled: isAuthenticated && !!occurrenceId,
    // A verdict for one opening of the join modal. Kept, a reopened modal
    // would show a stale answer (ALREADY_WAITLISTED after a leave) first.
    gcTime: 0,
  });
}

export function useClaimOffer(token: string | undefined) {
  return useQuery({
    queryKey: ['waitlist', 'claim', token],
    queryFn: async () => (await getClaimOffer(token!)) ?? null,
    enabled: !!token,
    refetchOnMount: true,
  });
}

// Holds change spendable balances, promotions change bookings and counts —
// one invalidation fan-out after every waitlist mutation.
export function invalidateWaitlistRelated(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['waitlist'] });
  queryClient.invalidateQueries({ queryKey: ['bookedClasses'] });
  queryClient.invalidateQueries({ queryKey: ['class_availabilities'] });
  queryClient.invalidateQueries({ queryKey: ['available_classes'] });
  queryClient.invalidateQueries({ queryKey: ['classGroups'] });
  queryClient.invalidateQueries({ queryKey: ['effective-price'] });
  queryClient.invalidateQueries({ queryKey: ['topupCodes'] });
  queryClient.invalidateQueries({ queryKey: ['classPackCodes'] });
  queryClient.invalidateQueries({ queryKey: ['subscriptionCodes'] });
}
